import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, Button, Chip, Alert, CircularProgress, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Avatar,
  MenuItem, Select, Card, CardContent, CardActions, Tabs, Tab, IconButton, FormControl, InputLabel, Radio, RadioGroup, FormControlLabel, FormHelperText, Stack,
  useMediaQuery, useTheme, Collapse, List, ListItem, ListItemText, Divider, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  AccountBalance, ApartmentOutlined, CampaignOutlined, ChevronRight, CreditCard, Download,
  EditOutlined, EventAvailableOutlined, GroupAddOutlined, HandymanOutlined, Money,
  PaymentsOutlined, SquareFootOutlined, StorefrontOutlined
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { paymentsAPI, complaintsAPI, usersAPI, maintenanceStaffAPI, notificationsAPI } from '../services/api';
import FacilityBooking from './FacilityBooking';
import CCAvPaymentCard from '../components/CCAvPaymentCard';
import { downloadPaymentReceipt } from '../utils/paymentReceipt';
import { getBillingLabels } from '../utils/billingLabels';

// Razorpay script loader
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) return resolve(true);
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const ProfileSection = () => {
  const { user } = useAuth();
  const attrs = user?.attributes || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper 
      sx={{ 
        display: { xs: 'none', md: 'flex' },
        p: { xs: 2, sm: 3 }, 
        mb: 3, 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'center', sm: 'center' }, 
        gap: { xs: 2, sm: 3 },
        textAlign: { xs: 'center', sm: 'left' },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }} 
      elevation={3}
    >
      <Avatar 
        sx={{ 
          width: { xs: 56, sm: 64 }, 
          height: { xs: 56, sm: 64 }, 
          bgcolor: 'primary.main', 
          fontSize: { xs: 28, sm: 32 }
        }}
      >
        {attrs.given_name ? attrs.given_name.charAt(0).toUpperCase() : '?'}
      </Avatar>
      <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            mb: { xs: 1, sm: 2 }
          }}
        >
          Profile
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: { xs: 0.5, sm: 0.75 }
        }}>
          <Typography sx={{ 
            fontSize: { xs: '14px', sm: '16px' },
            wordBreak: 'break-word',
            overflow: 'hidden'
          }}>
            <b>Name:</b> {attrs.given_name} {attrs.family_name}
          </Typography>
          <Typography sx={{ 
            fontSize: { xs: '14px', sm: '16px' },
            wordBreak: 'break-word',
            overflow: 'hidden'
          }}>
            <b>Email:</b> {attrs.email}
          </Typography>
          {attrs.phone_number && (
            <Typography sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              wordBreak: 'break-word',
              overflow: 'hidden'
            }}>
              <b>Phone:</b> {attrs.phone_number}
            </Typography>
          )}
          {attrs['custom:flat_id'] && (
            <Typography sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              wordBreak: 'break-word',
              overflow: 'hidden'
            }}>
              <b>Flat ID:</b> {attrs['custom:flat_id']}
            </Typography>
          )}
          {attrs['custom:building_id'] && (
            <Typography sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              wordBreak: 'break-word',
              overflow: 'hidden'
            }}>
              <b>Building ID:</b> {attrs['custom:building_id']}
            </Typography>
          )}
          <Typography sx={{ 
            fontSize: { xs: '14px', sm: '16px' },
            wordBreak: 'break-word',
            overflow: 'hidden'
          }}>
            <b>Role:</b> {Array.isArray(user.groups) ? user.groups.join(', ') : user.groups || 'No role assigned'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const useUserAssociations = () => {
  const { user } = useAuth();
  const userId = user?.attributes?.sub;

  return useQuery(
    ['userAssociations', userId],
    async () => {
      const response = await usersAPI.getAssociations(userId);
      return Array.isArray(response.data) ? response.data : [];
    },
    { enabled: !!userId, staleTime: 5 * 60 * 1000 }
  );
};

const MobileProfileSummary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: properties = [] } = useUserAssociations();
  const attrs = user?.attributes || {};
  const name = [attrs.given_name, attrs.family_name].filter(Boolean).join(' ') || user?.username || 'Resident';
  const buildingName = properties.find(property => property?.buildingName)?.buildingName || 'My Building';
  const role = (user?.groups || []).map(group => typeof group === 'string' ? group : group.name)
    .find(group => ['owner', 'tenant'].includes(group)) || user?.role || 'Resident';

  return (
    <Paper sx={{ display: { xs: 'flex', md: 'none' }, position: 'relative', alignItems: 'center', gap: 2, p: 2, mb: 2, overflow: 'hidden', borderRadius: 3, border: '1px solid #dbe7f3', background: 'linear-gradient(135deg, #fff 55%, #edf7ff 100%)', boxShadow: '0 8px 24px rgba(15,76,129,.08)', '@media (max-width:420px)': { display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr)', gap: 1.25, p: 1.5 }, '&::after': { content: '""', position: 'absolute', width: 130, height: 70, right: -28, bottom: -44, borderRadius: '50%', bgcolor: 'rgba(25,118,210,.08)' } }}>
      <Avatar sx={{ width: 64, height: 64, bgcolor: '#0b78e3', fontSize: 30, flexShrink: 0, '@media (max-width:420px)': { width: 56, height: 56, fontSize: 27 } }}>
        {name.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0, zIndex: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', textTransform: 'capitalize', '@media (max-width:420px)': { fontSize: '1rem', lineHeight: 1.25 } }}>Hello, {name}</Typography>
        <Typography color="text.secondary" noWrap sx={{ textTransform: 'capitalize' }}>{role} | {buildingName}</Typography>
        <Typography color="text.secondary" sx={{ mt: .35, fontSize: '.82rem' }}>Manage your home. Stay connected.</Typography>
      </Box>
      <Button onClick={() => navigate('/profile')} variant="text" size="small" startIcon={<EditOutlined />} sx={{ zIndex: 1, minWidth: 0, px: 1.2, py: .8, borderRadius: 5, bgcolor: 'rgba(25,118,210,.08)', fontWeight: 700, whiteSpace: 'nowrap', '@media (max-width:420px)': { gridColumn: '1 / -1', width: '100%', mt: .25 } }}>
        View Profile
      </Button>
    </Paper>
  );
};

const MobileQuickActions = () => {
  const navigate = useNavigate();
  const actions = [
    { label: 'Announcements', description: 'Stay updated with community news', icon: <CampaignOutlined />, color: '#ef476f', background: '#fde7ec', action: () => navigate('/announcements') },
    { label: 'Visitors', description: 'Invite and manage visitors easily', icon: <GroupAddOutlined />, color: '#8e24aa', background: '#f3e5f5', action: () => navigate('/visitors') },
    { label: 'Facilities Booking', description: 'Book and manage community facilities', icon: <EventAvailableOutlined />, color: '#008f7a', background: '#def5ef', action: () => navigate('/facility-booking') },
    { label: 'Complaints', description: 'Report and track maintenance issues', icon: <HandymanOutlined />, color: '#1565c0', background: '#e3f2fd', action: () => navigate('/?section=complaints') },
    { label: 'Make Payment', description: 'Pay maintenance and other dues', icon: <PaymentsOutlined />, color: '#ed8b00', background: '#fff3d6', action: () => navigate('/?section=payments') },
    { label: 'Community Marketplace', description: 'Buy, sell or avail community services', icon: <StorefrontOutlined />, color: '#7b1fa2', background: '#f3e5f5', action: () => navigate('/community-marketplace') },
  ];

  return (
    <Box sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25, mb: 2 }}>
      {actions.map(item => (
        <Paper
          key={item.label}
          component="button"
          type="button"
          onClick={item.action}
          sx={{
            position: 'relative', minHeight: 142, p: 1.5, overflow: 'hidden',
            border: '1px solid #e5e7eb', borderRadius: 3, bgcolor: '#fff',
            textAlign: 'left', cursor: 'pointer', boxShadow: '0 6px 18px rgba(15,23,42,.07)',
            transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
            WebkitTapHighlightColor: 'transparent',
            '&:active': { transform: 'scale(.98)', boxShadow: '0 2px 8px rgba(15,23,42,.08)' },
            '&:focus-visible': { outline: `3px solid ${item.background}`, borderColor: item.color },
            '@media (max-width:420px)': { minHeight: 170, p: 1.35 },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', '@media (max-width:420px)': { flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1 } }}>
            <Box sx={{ width: 52, height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2.75, color: item.color, background: `linear-gradient(145deg, ${item.background}, #fff)`, border: `1px solid ${item.background}`, boxShadow: `0 8px 18px ${item.background}`, '& .MuiSvgIcon-root': { fontSize: 31 } }}>
              {item.icon}
            </Box>
            <Box sx={{ minWidth: 0, pr: 4.5, '@media (max-width:420px)': { pr: 0, width: '100%' } }}>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.15, color: '#111827', overflowWrap: 'anywhere', '@media (max-width:420px)': { fontSize: '.92rem' } }}>{item.label}</Typography>
              <Typography sx={{ mt: .55, color: '#667085', fontSize: '.76rem', lineHeight: 1.35, overflowWrap: 'anywhere', '@media (max-width:420px)': { fontSize: '.7rem' } }}>{item.description}</Typography>
            </Box>
          </Box>
          <Box sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', color: item.color, bgcolor: item.background, '@media (max-width:420px)': { top: 16, transform: 'none', width: 32, height: 32 } }}><ChevronRight /></Box>
          <Box sx={{ position: 'absolute', width: 115, height: 58, right: -30, bottom: -35, borderRadius: '50%', bgcolor: item.background, opacity: .8 }} />
        </Paper>
      ))}
    </Box>
  );
};

const MobileRecentAnnouncements = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: properties = [] } = useUserAssociations();
  const userId = user?.userId || user?.attributes?.sub || user?.username;
  const buildingId = properties.find(property => property?.buildingId)?.buildingId;
  const { data: notifications = [], isLoading } = useQuery(
    ['userNotifications', userId, buildingId],
    () => notificationsAPI.getUserNotifications(userId, undefined, buildingId),
    {
      enabled: Boolean(userId && buildingId),
      select: response => Array.isArray(response.data) ? response.data : [],
      staleTime: 60000,
    }
  );
  const announcementTypes = new Set([
    'MAINTENANCE_SCHEDULED', 'EMERGENCY_MAINTENANCE', 'WATER_SUPPLY',
    'POWER_OUTAGE', 'ELEVATOR_MAINTENANCE', 'SECURITY_ALERT',
    'MEETING_NOTICE', 'FESTIVAL_CELEBRATION', 'VISITOR_POLICY',
    'AMENITY_BOOKING', 'GENERAL_ANNOUNCEMENT'
  ]);
  const recent = notifications
    .filter(notification => announcementTypes.has(notification.type))
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 3);

  return (
    <Paper sx={{ display: { xs: 'block', md: 'none' }, p: 1.75, mt: 2, mb: 'calc(88px + env(safe-area-inset-bottom, 0px))', borderRadius: 3, border: '1px solid #dfe7ef', boxShadow: '0 8px 24px rgba(15,23,42,.08)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignOutlined sx={{ color: '#ef476f', fontSize: 29 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>Recent Announcements</Typography>
        </Box>
        <Button size="small" endIcon={<ChevronRight />} onClick={() => navigate('/notifications-dashboard')} sx={{ minWidth: 0, px: .5, fontWeight: 800 }}>View All</Button>
      </Box>
      {isLoading && <Typography color="text.secondary">Loading announcements...</Typography>}
      {!isLoading && recent.length === 0 && <Typography color="text.secondary">No recent announcements.</Typography>}
      <Stack divider={<Divider flexItem />} sx={{ border: '1px solid #e5eaf0', borderRadius: 2.5, px: 1.25 }}>
        {recent.map((notification, index) => (
          <Box key={notification.notificationId} onClick={() => navigate('/notifications-dashboard', { state: { selectedNotificationId: notification.notificationId } })} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1.35, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
            <Avatar sx={{ width: 48, height: 48, bgcolor: index === 0 ? '#e3f2fd' : index === 1 ? '#def5ef' : '#fff3d6', color: index === 0 ? '#1976d2' : index === 1 ? '#008f7a' : '#ed8b00' }}><CampaignOutlined /></Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: '#111827' }} noWrap>{notification.title || 'Announcement'}</Typography>
              <Typography variant="caption" color="text.secondary">{notification.createdAt ? new Date(notification.createdAt).toLocaleDateString() : ''}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{notification.message || notification.description || ''}</Typography>
            </Box>
            <ChevronRight sx={{ color: '#667085' }} />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const SupervisorContactSection = () => {
  const { user } = useAuth();
  const { data: properties = [], isLoading: associationsLoading } = useUserAssociations();
  const claimedBuildingId = user?.attributes?.['custom:building_id'];
  const contactProperties = claimedBuildingId && !properties.some(property => property?.buildingId === claimedBuildingId)
    ? [{ buildingId: claimedBuildingId }, ...properties]
    : properties;
  const buildingIds = [...new Set(
    contactProperties.map(property => property?.buildingId).filter(Boolean)
  )].sort();
  const { data: contacts = [], isLoading } = useQuery(
    ['supervisorContacts', buildingIds],
    async () => {
      const uniqueBuildings = [...new Map(
        contactProperties
          .filter(property => property.buildingId)
          .map(property => [property.buildingId, property])
      ).values()];

      const results = await Promise.all(uniqueBuildings.map(async property => {
        try {
          const response = await maintenanceStaffAPI.getAll(property.buildingId);
          const staff = Array.isArray(response.data) ? response.data : (response.data?.items || []);
          return staff
            .filter(member => {
              const specialization = String(member.specialization || '').toUpperCase();
              return ['MANAGER', 'SUPERVISOR'].includes(specialization) &&
                member.isActive !== false && member.phoneNumber;
            })
            .map(member => {
              const specialization = String(member.specialization || '').toUpperCase();
              return {
                buildingId: property.buildingId,
                buildingName: property.buildingName || 'Building',
                supervisorId: member.staffId,
                supervisorName: member.name || (specialization === 'MANAGER' ? 'Manager' : 'Supervisor'),
                supervisorPhone: member.phoneNumber,
                contactRole: specialization === 'MANAGER' ? 'Manager' : 'Supervisor'
              };
            });
        } catch (error) {
          console.warn('Unable to load supervisor contact for building:', property.buildingId, error);
          return [];
        }
      }));
      return results.flat();
    },
    { enabled: buildingIds.length > 0, staleTime: 5 * 60 * 1000 }
  );

  if (isLoading || (associationsLoading && buildingIds.length === 0)) {
    return (
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: { xs: 3, sm: 2 }, border: { xs: '1px solid #dfe7ef', sm: 'none' } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.2rem' }, fontWeight: 700, mb: 1.5 }}>
          Building Manager / Supervisor
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">Loading contact details...</Typography>
        </Box>
      </Paper>
    );
  }

  if (contacts.length === 0) return null;

  return (
    <Paper elevation={2} sx={{ p: { xs: 1.75, sm: 2.5 }, mb: 3, borderRadius: { xs: 3, sm: 2 }, border: { xs: '1px solid #dfe7ef', sm: 'none' }, boxShadow: { xs: '0 8px 24px rgba(15,23,42,.08)', sm: undefined } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApartmentOutlined sx={{ display: { xs: 'block', sm: 'none' }, color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.2rem' }, fontWeight: 800, color: '#0f172a', '@media (max-width:420px)': { fontSize: '.95rem', lineHeight: 1.25 } }}>
            Building Manager / Supervisor
          </Typography>
        </Box>
        <Typography sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: .5, color: 'primary.main', fontSize: '.78rem', fontWeight: 800, whiteSpace: 'nowrap', '@media (max-width:420px)': { fontSize: '.68rem' } }}><PhoneIcon sx={{ fontSize: 17 }} />Call for assistance</Typography>
      </Box>
      <Stack spacing={1.5}>
        {contacts.map(contact => {
          const callablePhone = contact.supervisorPhone.replace(/[^\d+]/g, '');
          return (
            <Box
              key={`${contact.buildingId}-${contact.supervisorId}`}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                p: { xs: 1.5, sm: 1.5 },
                bgcolor: { xs: '#eef8ff', sm: 'action.hover' },
                background: { xs: 'linear-gradient(110deg, #eef8ff 0%, #f8fcff 100%)', sm: undefined },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                '@media (max-width:420px)': {
                  display: 'grid',
                  gridTemplateColumns: '52px minmax(0, 1fr)',
                  alignItems: 'center',
                  gap: 1.25,
                }
              }}
            >
              <Avatar sx={{ display: { xs: 'flex', sm: 'none' }, width: 62, height: 62, bgcolor: '#dbeefe', color: '#6d8fbd', fontSize: 25, flexShrink: 0, '@media (max-width:420px)': { width: 52, height: 52, fontSize: 22 } }}>
                {(contact.supervisorName || 'S').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 800, color: '#111827' }}>{contact.supervisorName || 'Supervisor'}</Typography>
                <Typography variant="body2" color="text.secondary">{contact.contactRole}</Typography>
                <Typography variant="body2" color="text.secondary">{contact.buildingName}</Typography>
                <Typography component="a" href={`tel:${callablePhone}`} sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}>
                  {contact.supervisorPhone}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<PhoneIcon />}
                component="a"
                href={`tel:${callablePhone}`}
                aria-label={`Call ${contact.supervisorName || `building ${contact.contactRole.toLowerCase()}`} at ${contact.supervisorPhone}`}
                sx={{ minHeight: 46, px: { xs: 1.5, sm: 2.5 }, flexShrink: 0, whiteSpace: 'nowrap', fontWeight: 800, borderRadius: 2, boxShadow: { xs: '0 6px 14px rgba(34,139,57,.22)', sm: undefined }, '@media (max-width:420px)': { gridColumn: '1 / -1', width: '100%', mt: .25 } }}
              >
                Call {contact.contactRole}
              </Button>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const complaintCategories = [
  { value: 'PLUMBING', label: 'Plumbing' },
  { value: 'ELECTRICAL', label: 'Electrical' },
  { value: 'CLEANING', label: 'Cleaning' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'OTHER', label: 'Other' },
];

const complaintPriorities = [
  { value: 'URGENT', label: 'Urgent', color: 'error' },
  { value: 'HIGH', label: 'High', color: 'warning' },
  { value: 'MEDIUM', label: 'Medium', color: 'info' },
  { value: 'LOW', label: 'Low', color: 'success' },
];

const CreateComplaintDialog = ({ open, onClose, flatId, buildingId }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const attrs = user?.attributes || {};
  const [form, setForm] = useState({
    userId: user?.userId || '',
    title: '',
    description: '',
    category: '',
    priority: 'MEDIUM',
    status: 'OPEN',
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        userId: user?.userId || '',
        title: '',
        description: '',
        category: '',
        priority: 'MEDIUM',
        status: 'OPEN',
      });
      setError('');
    }
  }, [open, user?.userId]);
  
  const createMutation = useMutation((data) => complaintsAPI.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('complaints');
      onClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.message || 'Failed to create complaint');
    },
  });
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    // Use the passed flatId prop, fallback to user's custom flat_id if not provided
    const complaintFlatId = flatId || attrs['custom:flat_id'];
    
    if (!complaintFlatId) {
      setError('Flat ID is required to create a complaint');
      return;
    }
    
    if (!buildingId) {
      setError('Building ID is required to create a complaint');
      return;
    }

    if (!form.category) {
      setError('Please select a complaint category');
      return;
    }
    
    createMutation.mutate({
      ...form,
      flatId: complaintFlatId,
      buildingId: buildingId,
      userId: user.username || user?.attributes?.sub,
    });
  };
  const selectedPriority = complaintPriorities.find((priority) => priority.value === form.priority);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: '100%' },
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 2.5 }
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.35rem' } }}>
          Create Complaint
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'primary.contrastText', opacity: 0.85 }}>
          Share the issue details so the building team can act on it quickly.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, bgcolor: 'grey.50' }}>
        <Box
          component="form"
          id="create-complaint-form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: { xs: 2, sm: 3 }
          }}
        >
          <TextField 
            label="Title" 
            name="title" 
            value={form.title} 
            onChange={handleChange} 
            fullWidth 
            required 
            size={isMobile ? "small" : "medium"}
            placeholder="Example: Water leakage in kitchen"
          />
          <TextField 
            label="Description" 
            name="description" 
            value={form.description} 
            onChange={handleChange} 
            fullWidth 
            required 
            multiline 
            rows={isMobile ? 3 : 4} 
            size={isMobile ? "small" : "medium"}
            placeholder="Add location, timing, and any details that help the team understand the issue."
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required size={isMobile ? "small" : "medium"}>
                <InputLabel id="complaint-category-label">Category</InputLabel>
                <Select
                  labelId="complaint-category-label"
                  id="complaint-category"
                  name="category"
                  value={form.category}
                  label="Category"
                  onChange={handleChange}
                >
                  <MenuItem value="" disabled>
                    Select category
                  </MenuItem>
                  {complaintCategories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Choose the closest issue type.</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required size={isMobile ? "small" : "medium"}>
                <InputLabel id="complaint-priority-label">Priority</InputLabel>
                <Select
                  labelId="complaint-priority-label"
                  id="complaint-priority"
                  name="priority"
                  value={form.priority}
                  label="Priority"
                  onChange={handleChange}
                  renderValue={(value) => {
                    const priority = complaintPriorities.find((item) => item.value === value);
                    return priority?.label || '';
                  }}
                >
                  {complaintPriorities.map((priority) => (
                    <MenuItem key={priority.value} value={priority.value}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={priority.label} color={priority.color} size="small" sx={{ minWidth: 72 }} />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Current selection: {selectedPriority?.label || 'Medium'}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                fontSize: { xs: '14px', sm: '16px' },
              }}
            >
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        px: { xs: 2, sm: 3 }, 
        py: { xs: 2, sm: 2.5 },
        gap: 1,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Button 
          onClick={onClose}
          disabled={createMutation.isLoading}
          size={isMobile ? "small" : "medium"}
          sx={{ fontSize: { xs: '12px', sm: '14px' }, px: 2 }}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          form="create-complaint-form"
          variant="contained" 
          disabled={createMutation.isLoading}
          size={isMobile ? "small" : "medium"}
          sx={{ fontSize: { xs: '12px', sm: '14px' }, px: 3, minWidth: 120 }}
        >
          {createMutation.isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};



// --- ComplaintsSection filtered by flatId ---
const ComplaintsSection = ({ flatId, flatNumber, buildingId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1800px)'); // Even more aggressive - force mobile layout up to 1800px
  
  const { data: complaintsResponse, isLoading, error } = useQuery(
    ['complaints', flatId],
    async () => {
      if (!flatId) return [];
      // Use the correct API to get complaints by flatId
      const res = await complaintsAPI.getByFlat(flatId);
      return Array.isArray(res.data) ? res.data : [];
    },
    { enabled: !!flatId }
  );
  const complaints = Array.isArray(complaintsResponse) ? complaintsResponse : [];
  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'error';
      case 'IN_PROGRESS': return 'warning';
      case 'RESOLVED': return 'success';
      case 'CLOSED': return 'default';
      default: return 'default';
    }
  };
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };
  if (isLoading) return <Typography>Loading complaints...</Typography>;
  if (error) return <Alert severity="error">Failed to load complaints: {error.message}</Alert>;
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Complaints</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => {
            // Trigger the complaint dialog from the parent component with flatId and buildingId
            const event = new CustomEvent('openComplaintDialog', { 
              detail: { flatId, buildingId } 
            });
            window.dispatchEvent(event);
          }}
          size="small"
          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
        >
          Create Complaint
        </Button>
      </Box>
      
      {complaints.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
          No complaints found for this flat. Click "Create Complaint" to file your first complaint.
        </Alert>
      ) : isMobile ? (
        // Mobile Card Layout
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {complaints.map((complaint) => (
            <Card key={complaint.complaintId} variant="outlined" sx={{ p: 1 }}>
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '14px', flex: 1, mr: 1 }}>
                    {complaint.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flexShrink: 0 }}>
                    <Chip 
                      label={complaint.priority || 'MEDIUM'} 
                      color={getPriorityColor(complaint.priority)} 
                      size="small" 
                      sx={{ fontSize: '10px', height: '20px' }}
                    />
                    <Chip 
                      label={complaint.status || 'OPEN'} 
                      color={getStatusColor(complaint.status)} 
                      size="small" 
                      sx={{ fontSize: '10px', height: '20px' }}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
                  {complaint.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ fontSize: '11px' }}>
                    Category: {complaint.category}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '11px' }}>
                    Flat: {complaint.flatNumber || flatNumber || complaint.flatId}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop Table Layout
        <TableContainer component={Paper} sx={{ 
          mb: 3,
          maxWidth: '100%',
          overflowX: 'auto'
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Flat Number</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {complaints.map((complaint) => (
                <TableRow key={complaint.complaintId}>
                  <TableCell>{complaint.title}</TableCell>
                  <TableCell>{complaint.description}</TableCell>
                  <TableCell>{complaint.category}</TableCell>
                  <TableCell>
                    <Chip label={complaint.priority || 'MEDIUM'} color={getPriorityColor(complaint.priority)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={complaint.status || 'OPEN'} color={getStatusColor(complaint.status)} size="small" />
                  </TableCell>
                  <TableCell>{complaint.flatNumber || flatNumber || complaint.flatId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};



const FlatsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { data: flatsResponse, isLoading, error } = useUserAssociations();
  const flats = useMemo(() => Array.isArray(flatsResponse) ? flatsResponse : [], [flatsResponse]);

  // Tabs state for each flat
  const [tabIndex, setTabIndex] = useState({});
  const [expandedFlatIds, setExpandedFlatIds] = useState([]);

  const handleTabChange = (flatId, event, newValue) => {
    setTabIndex(prev => ({ ...prev, [flatId]: newValue }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedSection = params.get('section');
    if (['payments', 'complaints'].includes(requestedSection) && flats.length > 0) {
      setExpandedFlatIds([flats[0].flatId]);
      setTabIndex(prev => ({ ...prev, [flats[0].flatId]: requestedSection === 'complaints' ? 1 : 0 }));
      document.getElementById('my-flats-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.search, flats]);

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );
  
  if (error) return (
    <Box>
      <Alert severity="error">
        {error.response?.status === 401
          ? 'Your session has expired. Please sign in again.'
          : `Failed to load flats: ${error.message}`}
      </Alert>
    </Box>
  );
  
  if (flats.length === 0) return (
    <Box>
      <Typography>No flats associated with your account.</Typography>
    </Box>
  );

  return (
    <Box id="my-flats-section" sx={{
      mb: 3,
      p: { xs: 1.75, sm: 0 },
      border: { xs: '1px solid #dfe7ef', sm: 'none' },
      borderRadius: { xs: 3, sm: 0 },
      bgcolor: { xs: '#fff', sm: 'transparent' },
      boxShadow: { xs: '0 8px 24px rgba(15,23,42,.08)', sm: 'none' },
      mx: 'auto',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.25, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApartmentOutlined sx={{ display: { xs: 'block', sm: 'none' }, color: 'primary.main', fontSize: 30 }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1.2rem', sm: '1.25rem' }, fontWeight: { xs: 800, sm: 600 }, color: '#0f172a' }}>My Flats</Typography>
        </Box>
        <Button
          size="small"
          endIcon={<ChevronRight />}
          onClick={() => setExpandedFlatIds(flats.map(flat => flat.flatId))}
          sx={{ display: { xs: 'flex', sm: 'none' }, minWidth: 0, px: 0.5, fontWeight: 800 }}
        >
          View All
        </Button>
      </Box>
      {flats.map(flat => (
        <Accordion
          key={flat.flatId}
          expanded={expandedFlatIds.includes(flat.flatId)}
          onChange={(_, expanded) => setExpandedFlatIds(current => expanded
            ? [...new Set([...current, flat.flatId])]
            : current.filter(flatId => flatId !== flat.flatId))}
          sx={{
            mb: 1,
            border: { xs: '1px solid #e2e8f0', sm: 'none' },
            borderRadius: { xs: '12px !important', sm: 0 },
            boxShadow: { xs: 'none', sm: undefined },
            overflow: 'hidden',
            '&::before': { display: { xs: 'none', sm: 'block' } },
            '& .MuiAccordionDetails-root': {
              overflowX: 'hidden'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              px: { xs: 1.25, sm: 2 },
              py: { xs: 1.4, sm: 1.5 },
              minHeight: { xs: 106, sm: 48 },
              overflowX: 'hidden',
              '& .MuiAccordionSummary-expandIconWrapper': {
                width: { xs: 40, sm: 'auto' }, height: { xs: 40, sm: 'auto' },
                alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
                color: { xs: 'primary.main', sm: 'inherit' }, bgcolor: { xs: '#e8f4ff', sm: 'transparent' }
              },
              '& .MuiAccordionSummary-content': {
                display: { xs: 'grid', sm: 'flex' },
                gridTemplateColumns: { xs: '58px minmax(0, 1fr)', sm: 'none' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                columnGap: { xs: 1.25, sm: 0 },
                overflow: 'hidden',
                minWidth: 0
              }
            }}
          >
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gridRow: '1 / span 2', width: 58, height: 70, alignItems: 'center', justifyContent: 'center', borderRadius: 2.5, bgcolor: '#e5f2ff', color: '#5f91ca' }}>
              <ApartmentOutlined sx={{ fontSize: 39 }} />
            </Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                flex: 1,
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: { xs: 800, sm: 'medium' },
                mb: { xs: 0.5, sm: 0 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                minWidth: 0,
                color: { xs: '#111827', sm: 'inherit' }
              }}
            >
              Flat #{flat.flatNumber} &mdash; {flat.buildingName}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: { xs: 0.5, sm: 1 },
              overflow: 'hidden',
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiChip-root': {
                fontSize: { xs: '10px', sm: '12px' },
                height: { xs: '24px', sm: '24px' },
                maxWidth: { xs: '145px', sm: '150px' },
                fontWeight: 600
              }
            }}>
              <Chip icon={<ApartmentOutlined />} label={`${flat.flatType}B`} size="small" sx={{ bgcolor: '#eaf5ff', color: '#155fa0' }} />
              <Chip icon={<SquareFootOutlined />} label={`Area: ${flat.area} sq ft`} size="small" sx={{ bgcolor: '#f3f4f6' }} />
              <Chip label={`${getBillingLabels(flat.billingMode).amount}: ₹${flat.monthlyMaintenance}`} size="small" />
            </Box>
          </AccordionSummary>
          {expandedFlatIds.includes(flat.flatId) && (
            <AccordionDetails sx={{
              px: { xs: 1, sm: 2 },
              overflowX: 'hidden', // Prevent horizontal overflow in accordion content
              width: '100%',
              boxSizing: 'border-box'
            }}>
            <Tabs
              value={tabIndex[flat.flatId] || 0}
              onChange={(e, v) => handleTabChange(flat.flatId, e, v)}
              sx={{ 
                mb: 2,
                '& .MuiTab-root': {
                  fontSize: { xs: '12px', sm: '14px' },
                  minWidth: { xs: 'auto', sm: 160 },
                  padding: { xs: '6px 12px', sm: '12px 16px' }
                }
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Payments" />
              <Tab label="Complaints" />
            </Tabs>
            {((tabIndex[flat.flatId] || 0) === 0) && (
              <Box sx={{ width: '100%', overflowX: 'hidden' }}>
                <FlatPaymentsTable flat={flat} />
              </Box>
            )}
            {((tabIndex[flat.flatId] || 0) === 1) && (
              <Box sx={{ width: '100%', overflowX: 'hidden' }}>
                <ComplaintsSection
                  flatId={flat.flatId || flat.flatID}
                  flatNumber={flat.flatNumber}
                  buildingId={flat.buildingId || flat.buildingID}
                />
              </Box>
            )}
            </AccordionDetails>
          )}
        </Accordion>
      ))}
    </Box>
  );
};

// --- FlatPaymentsTable for payments per flat ---
const FlatPaymentsTable = ({ flat }) => {
  // Outstanding balance payment state
  const [payingOutstanding, setPayingOutstanding] = useState(false);
  const [outstandingSuccess, setOutstandingSuccess] = useState(false);
  const [outstandingError, setOutstandingError] = useState('');
  const [outstandingPaid, setOutstandingPaid] = useState(flat.outstandingBalance === 0);
  
  // Payment state
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CCAvenue');
  const [paymentError, setPaymentError] = useState('');
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [paymentConfirmationType, setPaymentConfirmationType] = useState('');
  const [paymentConfirmationMessage, setPaymentConfirmationMessage] = useState('');
  const [ccavenuePaymentAmount, setCcavenuePaymentAmount] = useState(0);
  
  // CCAvenue payment card state
  const [showCCAvCard, setShowCCAvCard] = useState(false);

  useEffect(() => {
    setPaymentAmount(flat.outstandingBalance ? flat.outstandingBalance.toString() : '');
    setPaymentError('');
  }, [flat.flatId, flat.outstandingBalance]);

  const queryClient = useQueryClient();
  // State to show/hide payment history
  const [showHistory, setShowHistory] = useState(false);
  // --- ADDED STATE AND HANDLERS ---
  // Define fetchPayments at the top so it is in scope for all handlers - using single API call
  const fetchPayments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Validate required data
      const buildingId = flat.buildingId || flat.buildingID;
      if (!buildingId || !flatId) {
        console.warn('Missing required data for fetchPayments:', { buildingId, flatId });
        setPayments([]);
        return;
      }
      
      // Make a single API call to get all payments for the building and flat
      const response = await paymentsAPI.getByBuildingAndFlat(buildingId, flatId);
      const allPayments = Array.isArray(response.data) ? response.data : [];

      // Filter payments to last 12 months
      const now = new Date();
      const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      
      const filteredPayments = allPayments.filter(payment => {
        // Check if payment has year/month properties
        if (payment.year && payment.month) {
          const paymentDate = new Date(payment.year, payment.month - 1, 1);
          return paymentDate >= twelveMonthsAgo;
        }
        
        // Fallback to createdAt if year/month not available
        if (payment.createdAt) {
          const paymentDate = new Date(payment.createdAt);
          return paymentDate >= twelveMonthsAgo;
        }
        
        return false;
      });

      // Sort payments in descending order by created date (most recent first)
      const sortedPayments = filteredPayments.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.paidDate || 0);
        const dateB = new Date(b.createdAt || b.paidDate || 0);
        return dateB - dateA;
      });

      setPayments(sortedPayments);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };
  const { user } = useAuth(); // <-- Add this line at the top of the component
  const receiptResidentName = user?.fullName || user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const flatId = flat.flatId;
  const monthlyMaintenance = flat.monthlyMaintenance;
  // --- ADDED STATE AND HANDLERS ---
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);
  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Validate required data
        const buildingId = flat.buildingId || flat.buildingID;
        if (!buildingId || !flatId) {
          console.warn('Missing required data for fetchPayments:', { buildingId, flatId });
          setPayments([]);
          return;
        }
        
        // Make a single API call to get all payments for the building and flat
        const response = await paymentsAPI.getByBuildingAndFlat(buildingId, flatId);
        const allPayments = Array.isArray(response.data) ? response.data : [];

        // Filter payments to last 12 months (consistent with fetchPayments function)
        const now = new Date();
        const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        
        const filteredPayments = allPayments.filter(payment => {
          // Check if payment has year/month properties
          if (payment.year && payment.month) {
            const paymentDate = new Date(payment.year, payment.month - 1, 1);
            return paymentDate >= twelveMonthsAgo;
          }
          
          // Fallback to createdAt if year/month not available
          if (payment.createdAt) {
            const paymentDate = new Date(payment.createdAt);
            return paymentDate >= twelveMonthsAgo;
          }
          
          return false;
        });

        // Sort payments in descending order by created date (most recent first)
        const sortedPayments = filteredPayments.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.paidDate || 0);
          const dateB = new Date(b.createdAt || b.paidDate || 0);
          return dateB - dateA;
        });
        
        setPayments(sortedPayments);
        setCurrentPage(1); // Reset page when flat changes
      } catch (err) {
        console.error('Error in fetchPayments:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    if (flatId) fetchPayments();
  }, [flatId, flat.buildingId, flat.buildingID]);

  // Show payment history state
  const now = new Date();

  // Compose rows: show payment history + pending rows for unpaid months
  // Build last twelve months for payment history
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  const monthsToCheck = [];
  for (let i = 0; i < 12; i++) {
    monthsToCheck.unshift({ year: y, month: m });
    m--;
    if (m === 0) {
      m = 12;
      y--;
    }
  }
  // Map payments by year+month for quick lookup, also include all completed payments
  const paymentMap = {};
  const allCompletedPayments = [];
  
  payments.forEach(p => {
    if (p.status === 'COMPLETED') {
      allCompletedPayments.push(p);
    }
    if (p.year && p.month) {
      paymentMap[`${p.year}-${p.month}`] = p;
    }
  });
  
  // Show latest 6 completed payments, newest-first
  const sortedCompleted = [...allCompletedPayments].sort((a, b) => (Date.parse(b.createdAt || b.paidDate || 0) || 0) - (Date.parse(a.createdAt || a.paidDate || 0) || 0));
  const latestSix = sortedCompleted.slice(0, 6);
  const paymentRows = latestSix.map(payment => ({ type: 'completed', payment }));
  const totalPages = 1; // No pagination for tenant view, showing only latest 6
  
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };


  const handleRazorpayPayment = async (flat, amount, paymentMethod) => {
    try {
      setPayingOutstanding(true);
      
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setOutstandingError('Razorpay SDK failed to load. Please try again.');
        setPayingOutstanding(false);
        return;
      }
      
      const currentDate = new Date();
      const orderRes = await paymentsAPI.create({
        amount: amount, // Don't multiply by 100 - backend handles conversion
        currency: 'INR',
        flatId: flat.flatId,
        buildingId: flat.buildingId || flat.buildingID,
        userId: user.userId,
        paymentType: "OutstandingBalance",
        paymentMethod: "RAZOR PAY",
        description: "Outstanding balance payment",
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1
      });
    const responseData = orderRes.data;
    const order = responseData.order; // Extract the order object from the response
    // Validate that building has Razorpay credentials configured
    if (!order.key_id) {
      throw new Error('Razorpay payment is not configured for this building. Please contact administrator to set up payment gateway.');
    }
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: 'Apartment Maintenance',
      description: 'Maintenance Payment',
      order_id: order.id,
      handler: async function (response) {
        try {
          await paymentsAPI.update(responseData.paymentId, {
            paymentId: responseData.paymentId,
            flatId: flat.flatId,
            status: 'COMPLETED',
            amount: amount,
            description: 'Payment completed via Razorpay',
            razorpayPaymentId: response.razorpay_payment_id,
            razorpayOrderId: response.razorpay_order_id,
            razorpaySignature: response.razorpay_signature,
            buildingId: flat.buildingId || flat.buildingID,
            paymentMethod
          });
          
          setOutstandingSuccess(true);
          
          // Only mark as fully paid if payment amount equals or exceeds outstanding balance
          const outstandingAmount = Number(flat.outstandingBalance || 0);
          if (amount >= outstandingAmount) {
            setOutstandingPaid(true);
          } else {
            // For partial payments, update the flat's outstanding balance
            flat.outstandingBalance = Math.max(0, outstandingAmount - amount);
            setOutstandingPaid(flat.outstandingBalance === 0);
          }
          
          await fetchPayments();
          
          // Reset pagination to first page to show the new payment
          setCurrentPage(1);
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setOutstandingSuccess(false);
          }, 5000);
          
        } catch (error) {
          console.error('Payment completion error:', error);
          setOutstandingError('Payment completed but verification failed. Please contact support.');
        }
      },
      prefill: {
        name: user.firstName + ' ' + user.lastName,
        email: user.email,
        contact: user.phoneNumber
      },
      theme: { color: "#528FF0" }
    };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setPayingOutstanding(false);
      
    } catch (error) {
      console.error('Razorpay payment error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      
      let errorMessage = 'Payment initiation failed. Please try again.';
      if (error.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later or contact support.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setOutstandingError(errorMessage);
      setPayingOutstanding(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1800px)'); // Even more aggressive - force mobile layout up to 1800px

  // Process payment function to handle actual payment logic
  const processPayment = async () => {
    const amount = Number(paymentAmount);
    const outstandingAmount = Number(flat.outstandingBalance || 0);
    
    setPaymentError('');
    setOutstandingSuccess(false);
    setPayingOutstanding(true);
    
    try {
      let description;
      if (amount === outstandingAmount) {
        description = "Outstanding balance paid in full";
      } else {
        description = `Partial payment of ₹${amount} towards outstanding balance`;
      }
      
      const currentDate = new Date();
      await paymentsAPI.create({
        flatId: flat.flatId,
        buildingId: flat.buildingId || flat.buildingID,
        userId: user?.username || user?.attributes?.sub,
        amount: amount,
        paymentType: 'OutstandingBalance',
        paymentMethod: paymentMethod,
        status: 'COMPLETED',
        description: description,
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1,
        createdAt: currentDate.toISOString()
      });
      
      setOutstandingSuccess(true);
      setPaymentError('');
      setPaymentAmount('');
      
      // Refresh payments data with a shorter delay to show cash payment immediately
      setTimeout(async () => {
        await fetchPayments();
        setCurrentPage(1); // Reset pagination to show the new payment first
        queryClient.invalidateQueries('userFlats');
      }, 300);
    } catch (error) {
      setPaymentError('Failed to process payment: ' + (error.response?.data?.message || error.message));
    } finally {
      setPayingOutstanding(false);
    }
  };

  if (isLoading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">Failed to load payments: {error.message}</Alert>;
  
  // Temporary debug message


  return (
    <Box>
      {/* Outstanding balance section with friendly message */}
      {flat.outstandingBalance > 0 && !outstandingPaid ? (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
            Outstanding Balance (incl. current month): ₹{flat.outstandingBalance}
          </Alert>
          
          {/* Payment form - hide when CCAvenue card is shown */}
          {!showCCAvCard && (
         <Box sx={{ 
           display: 'flex', 
           alignItems: 'center', 
           gap: { xs: 1, sm: 2 }, 
           flexWrap: 'wrap',
           flexDirection: { xs: 'column', sm: 'row' },
           alignItems: { xs: 'stretch', sm: 'center' },
           width: '100%'
         }}>
            <Typography variant="body2" sx={{ 
              mr: { xs: 0, sm: 1 }, 
              fontSize: { xs: '14px', sm: '16px' },
              mb: { xs: 1, sm: 0 },
              alignSelf: 'flex-start',
              mt: { xs: 0, sm: 1 }
            }}>
              Payment Amount:
            </Typography>
            
            <TextField
              size="small"
              type="number"
              value={paymentAmount}
              onChange={(e) => {
                setPaymentAmount(e.target.value);
                setPaymentError('');
              }}
              inputProps={{ 
                min: 0, 
                max: flat.outstandingBalance,
                step: 0.01
              }}
              sx={{ 
                width: { xs: '100%', sm: '150px' },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '14px', sm: '16px' }
                }
              }}
            />
            
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              width: { xs: '100%', sm: 'auto' }
            }}>
              {/* Cash payment option removed for Owner/Tenant dashboard */}
              <FormControlLabel
                control={
                  <Radio
                    checked={paymentMethod === 'Razorpay'}
                    disabled
                    onChange={() => {
                      if (!paymentAmount) {
                        setPaymentAmount(flat.outstandingBalance.toString());
                      }
                      setPaymentMethod('Razorpay');
                      setPaymentError('');
                    }}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CreditCard sx={{ fontSize: '18px', color: 'text.disabled' }} />
                    <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, color: 'text.disabled' }}>Razorpay</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={paymentMethod === 'CCAvenue'}
                    onChange={() => {
                      if (!paymentAmount) {
                        setPaymentAmount(flat.outstandingBalance.toString());
                      }
                      setPaymentMethod('CCAvenue');
                      setPaymentError('');
                    }}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccountBalance sx={{ fontSize: '18px', color: '#ff9800' }} />
                    <Typography sx={{ fontSize: { xs: '12px', sm: '14px' } }}>CCAvenue</Typography>
                  </Box>
                }
              />
            </Box>
            
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const currentAmount = paymentAmount;
                const currentMethod = paymentMethod || 'CCAvenue';
                
                setPaymentError('');
                
                if (!currentAmount || isNaN(currentAmount)) {
                  setPaymentError('Please enter a valid payment amount.');
                  return;
                }
                
                const amount = Number(currentAmount);
                const outstandingAmount = Number(flat.outstandingBalance || 0);
                
                if (amount <= 0) {
                  setPaymentError('Payment amount must be greater than 0.');
                  return;
                }
                
                if (amount > outstandingAmount) {
                  setPaymentError(`Payment amount cannot exceed outstanding balance of ₹${outstandingAmount}.`);
                  return;
                }
                
                // Handle payment scenarios for all methods
                  if (amount === outstandingAmount) {
                  // Scenario A: Exact outstanding amount - proceed directly
                  if (currentMethod === 'Razorpay') {
                    handleRazorpayPayment(flat, amount, 'Online');
                  } else if (currentMethod === 'CCAvenue') {
                    setCcavenuePaymentAmount(amount);
                    setShowCCAvCard(true);
                  } else {
                    // Fallback: treat unknown method as online
                    handleRazorpayPayment(flat, amount, 'Online');
                  }
                } else {
                  // Scenario B: Less than outstanding - show confirmation
                  const remainingBalance = outstandingAmount - amount;
                  setPaymentConfirmationType('partial');
                  setPaymentConfirmationMessage(
                    `You are paying ₹${amount} against an outstanding balance of ₹${outstandingAmount}. ` +
                    `The remaining balance will be ₹${remainingBalance}. Do you want to continue?`
                  );
                  // Store current method and amount for confirmation callback
                  setPaymentMethod(currentMethod);
                  setPaymentAmount(amount.toString());
                  setShowPaymentConfirmation(true);
                }
              }}
              disabled={payingOutstanding || outstandingPaid}
              sx={{ 
                fontSize: { xs: '11px', sm: '13px' },
                minWidth: { xs: '100%', sm: '100px' },
                whiteSpace: 'nowrap',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 }
              }}
            >
              Pay Now
            </Button>
          </Box>
          )}
          
          {/* Show payment error if any */}
          {paymentError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '12px', sm: '14px' } }}>
              {paymentError}
            </Alert>
          )}
        </Box>
      ) : (
        <Alert severity="success" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
          🎉 Great! You don't have any outstanding dues for this flat.
        </Alert>
      )}
      
      {outstandingError && (
        <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
          {outstandingError}
        </Alert>
      )}
      
      {outstandingSuccess && (
        <Alert severity="success" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
          Payment processed successfully! Your payment will be reflected shortly.
        </Alert>
      )}

      {/* CCAvenue Payment Card - Display immediately after payment buttons */}
      {showCCAvCard && (
        <Box sx={{ mb: 3 }}>
          <CCAvPaymentCard 
            flat={flat}
            amount={ccavenuePaymentAmount || flat.outstandingBalance}
            onClose={() => {
              setShowCCAvCard(false);
              setCcavenuePaymentAmount(0); // Reset stored amount
            }}
            onPaymentError={(error) => {
              console.log('CCAvenue payment failed:', error);
              setOutstandingError(error);
              setShowCCAvCard(false);
              setCcavenuePaymentAmount(0); // Reset stored amount
            }}
          />
        </Box>
      )}
      

      {isMobile ? (
        // Mobile Card Layout
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {paymentRows.length === 0 ? (
            <Alert severity="info" sx={{ fontSize: '14px' }}>
              No completed payments found for this flat.
            </Alert>
          ) : (
            paymentRows.map((row, idx) => {
              if (row.type === 'completed') {
                const payment = row.payment;
                return (
                  <Card key={payment.paymentId} variant="outlined" sx={{ p: 1 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                          ₹{payment.amount}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip 
                            label={payment.status} 
                            color="success" 
                            size="small" 
                            sx={{ fontSize: '10px', height: '20px' }}
                          />
                          <Chip 
                            label="Paid" 
                            color="success" 
                            size="small" 
                            sx={{ fontSize: '10px', height: '20px' }}
                          />
                        </Box>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '12px', 
                          color: 'text.secondary', 
                          mb: 1,
                          wordBreak: 'break-all',
                          lineHeight: 1.2,
                          cursor: payment.paymentId.length > 20 ? 'pointer' : 'default'
                        }}
                        onClick={() => {
                          if (payment.paymentId.length > 20) {
                            navigator.clipboard?.writeText(payment.paymentId);
                            // You could add a toast notification here
                          }
                        }}
                        title={payment.paymentId.length > 20 ? 'Click to copy full ID' : payment.paymentId}
                      >
                        ID: {payment.paymentId.length > 20 
                          ? `${payment.paymentId.substring(0, 20)}...` 
                          : payment.paymentId
                        }
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '11px' }}>
                          Type: {payment.paymentType}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '11px' }}>
                          Method: {payment.paymentMethod}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                          Date: {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : ''}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={() => downloadPaymentReceipt(payment, {
                            flat,
                            buildingName: flat.buildingName,
                            residentName: receiptResidentName
                          })}
                          sx={{ whiteSpace: 'nowrap', minWidth: 104 }}
                        >
                          Receipt
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              }
              return null;
            })
          )}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Box>
      ) : (
        // Desktop Table Layout
        <>
          <TableContainer component={Paper} sx={{ 
            mb: 2,
            maxWidth: '100%',
            overflowX: 'auto',
            '& .MuiTable-root': {
              width: '100%',
              maxWidth: '100%',
              tableLayout: 'fixed' // Use fixed layout for better column control
            }
          }}>
          <Table size="small" sx={{ minWidth: 880, tableLayout: 'fixed', '& th, & td': { verticalAlign: 'middle' } }}>
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  width: '18%', 
                  maxWidth: '120px',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Payment ID
                </TableCell>
                <TableCell sx={{ 
                  width: '14%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Amount
                </TableCell>
                <TableCell sx={{ 
                  width: '12%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Type
                </TableCell>
                <TableCell sx={{ 
                  width: '13%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Method
                </TableCell>
                <TableCell sx={{ 
                  width: '13%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Status
                </TableCell>
                <TableCell sx={{ 
                  width: '18%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Date
                </TableCell>
                <TableCell sx={{ 
                  width: '12%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No completed payments found for this flat.</TableCell>
                </TableRow>
              ) : (
                paymentRows.map((row, idx) => {
                  if (row.type === 'completed') {
                    const payment = row.payment;
                    return (
                      <TableRow key={payment.paymentId}>
                        <TableCell sx={{ 
                          maxWidth: { xs: '70px', sm: '100px' }, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          fontSize: { xs: '12px', sm: '14px' }
                        }}
                        onClick={() => navigator.clipboard?.writeText(payment.paymentId)}
                        title={`${payment.paymentId} (Click to copy)`}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ fontSize: '13px' }}
                          >
                            {payment.paymentId}
                          </Typography>
                        </TableCell>
                        <TableCell>₹{payment.amount}</TableCell>
                        <TableCell>{payment.paymentType}</TableCell>
                        <TableCell>{payment.paymentMethod}</TableCell>
                        <TableCell>
                          <Chip label={payment.status} color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          {payment.createdAt
                            ? new Date(payment.createdAt).toLocaleDateString()
                            : ''}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => downloadPaymentReceipt(payment, {
                              flat,
                              buildingName: flat.buildingName,
                              residentName: receiptResidentName
                            })}
                            sx={{ whiteSpace: 'nowrap', minWidth: 104 }}
                          >
                            Receipt
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return null;
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination 
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
        </>
      )}
      
      {/* Payment Confirmation Dialog */}
      <Dialog 
        open={showPaymentConfirmation} 
        onClose={() => setShowPaymentConfirmation(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Confirm Partial Payment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {paymentConfirmationMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentConfirmation(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowPaymentConfirmation(false);
              const amount = Number(paymentAmount);
              
              if (paymentMethod === 'Razorpay') {
                handleRazorpayPayment(flat, amount, 'Online');
              } else if (paymentMethod === 'CCAvenue') {
                setCcavenuePaymentAmount(amount);
                setShowCCAvCard(true);
              } else {
                processPayment();
              }
            }}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>
      
    </Box>
  );
};

const FacilityBookingCard = ({ onOpen }) => (
  <Card sx={{ minWidth: 275 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <EventAvailableIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
        <Typography variant="h6" component="div">
          Facility Booking
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Book amenities like clubhouse, gym, or other facilities available in your apartment complex.
      </Typography>
    </CardContent>
    <CardActions>
      <Button variant="contained" color="primary" onClick={onOpen}>
        Book a Facility
      </Button>
    </CardActions>
  </Card>
);

const OwnerTenantDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [complaintDialogOpen, setComplaintDialogOpen] = useState(false);
  const [facilityBookingOpen, setFacilityBookingOpen] = useState(false);
  const [currentFlatId, setCurrentFlatId] = useState(null);
  const [currentBuildingId, setCurrentBuildingId] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Check for payment success parameter and show success message
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('payment') === 'success') {
      setPaymentSuccess(true);
      // Remove the parameter from URL without reload
      window.history.replaceState({}, '', '/');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setPaymentSuccess(false);
      }, 5000);
    }
  }, [location.search]);

  // Listen for the custom event to open complaint dialog
  useEffect(() => {
    const handleOpenComplaintDialog = (event) => {
      const flatId = event.detail?.flatId;
      const buildingId = event.detail?.buildingId;
      setCurrentFlatId(flatId);
      setCurrentBuildingId(buildingId);
      setComplaintDialogOpen(true);
    };

    window.addEventListener('openComplaintDialog', handleOpenComplaintDialog);
    
    return () => {
      window.removeEventListener('openComplaintDialog', handleOpenComplaintDialog);
    };
  }, []);

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: { xs: '100%', sm: '100%', md: '1200px', lg: '1400px' },
      mx: 'auto',
      px: { xs: 1, sm: 2, md: 3 },
      py: { xs: 1, sm: 2 },
      overflowX: 'hidden',
      boxSizing: 'border-box',
      minWidth: 0,
      '& > *': {
        maxWidth: '100%',
        boxSizing: 'border-box'
      },
      '& .MuiPaper-root, & .MuiAccordion-root, & .MuiCard-root': {
        maxWidth: '100% !important',
        overflow: 'hidden',
        boxSizing: 'border-box'
      },
      '& .MuiTableContainer-root': {
        maxWidth: '100% !important',
        overflowX: 'auto',
        boxSizing: 'border-box'
      },
      '& .MuiTable-root': {
        maxWidth: '100% !important',
        tableLayout: 'fixed'
      },
      '& .MuiAccordionDetails-root': {
        overflowX: 'hidden',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }
    }}>
      {/* Payment Success Message */}
      {paymentSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '14px', sm: '16px' },
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 20 }} />
          🎉 Payment completed successfully! Your payment has been processed and your account has been updated.
        </Alert>
      )}
      
      <MobileProfileSummary />
      <MobileQuickActions />
      <ProfileSection />
      <SupervisorContactSection />
      {/* Feature Cards */}
      {/* <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FacilityBookingCard onOpen={() => setFacilityBookingOpen(true)} />
        </Grid>
        {/* You can add more feature cards here */}
      {/* </Grid> */}
      <FlatsSection />
      <MobileRecentAnnouncements />
      {/* Facility Booking Dialog - Commented out for now */}
      {/* <Dialog
        open={facilityBookingOpen}
        onClose={() => setFacilityBookingOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <IconButton
            aria-label="close"
            onClick={() => setFacilityBookingOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            ×
          </IconButton>
          <FacilityBooking />
        </Box>
      </Dialog> */}
      <CreateComplaintDialog 
        open={complaintDialogOpen} 
        onClose={() => setComplaintDialogOpen(false)} 
        flatId={currentFlatId}
        buildingId={currentBuildingId}
      />
    </Box>
  );
};

export default OwnerTenantDashboard;
